// Pure helpers for the Team tab: filtering, sorting, and the per-row display strings. Kept
// free of React so the logic is unit-testable in isolation — the component just composes
// these. The grid "available shifts" lookup lives in @load-balancing/services.
import type {
  ScheduleTeamFteFilterType,
  ScheduleTeamMemberType,
  ScheduleTeamRoleFilterType,
  ScheduleTeamUnitFilterType,
} from '@load-balancing/types';

// Employment / shift labels are shared with the worker profile panel.
export {
  getEmploymentLabel,
  getShiftPatternLabel,
  getShiftTypeLabel,
} from '@load-balancing/services';
// Hours formatter shared with the worker profile panel / team-review roster, so the FTE
// tooltip rounds hours the same way ("80h") everywhere.
import { fteHoursLabel } from '@load-balancing/services';

/** Roster sorted alphabetically by name for scanability. */
export function sortTeamByName(
  members: ScheduleTeamMemberType[]
): ScheduleTeamMemberType[] {
  return [...members].sort((a, b) => a.name.localeCompare(b.name));
}

export function filterTeamByRole(
  members: ScheduleTeamMemberType[],
  role: ScheduleTeamRoleFilterType
): ScheduleTeamMemberType[] {
  if (role === 'all') return members;
  return members.filter((member) => member.role === role);
}

/**
 * Role filter options derived from the roster: "All roles", then each distinct clinical
 * role present, sorted. Sourcing the options from the same data the filter matches
 * guarantees a selected role always matches member.role (no hardcoded RN/LPN/UAP drift,
 * and the unit's real abbreviations — e.g. Unit Secretary — appear).
 */
export function roleFilterOptions(
  members: ScheduleTeamMemberType[]
): { value: ScheduleTeamRoleFilterType; label: string }[] {
  const roles = [
    ...new Set(members.map((member) => member.role).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));

  return [
    { value: 'all', label: 'All roles' },
    ...roles.map((role) => ({ value: role, label: role })),
  ];
}

export function filterTeamByFte(
  members: ScheduleTeamMemberType[],
  fte: ScheduleTeamFteFilterType
): ScheduleTeamMemberType[] {
  if (fte === 'above') return members.filter(isAboveFte);
  if (fte === 'below') return members.filter(isBelowFte);
  return members;
}

export function filterTeamByUnit(
  members: ScheduleTeamMemberType[],
  unit: ScheduleTeamUnitFilterType
): ScheduleTeamMemberType[] {
  if (unit === 'all') return members;
  return members.filter((member) => member.unit === unit);
}

/** Case-insensitive name match; an empty query passes everyone through. */
export function filterTeamByQuery(
  members: ScheduleTeamMemberType[],
  query: string
): ScheduleTeamMemberType[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return members;
  return members.filter((member) =>
    member.name.toLowerCase().includes(normalized)
  );
}

/**
 * Limits the roster to a specific set of worker ids (the member `id` is the worker uuid). An
 * empty set passes everyone through — a "View in Team" Needs Attention card sets this to the
 * exact staff it flagged so the roster names the same people the banner counted.
 */
export function filterTeamByWorkerIds(
  members: ScheduleTeamMemberType[],
  workerIds: readonly string[]
): ScheduleTeamMemberType[] {
  if (workerIds.length === 0) return members;
  const ids = new Set(workerIds);
  return members.filter((member) => ids.has(member.id));
}

/** The browsable roster: members homed to the unit under review (excludes visitors). */
export function homeUnitMembers(
  members: ScheduleTeamMemberType[]
): ScheduleTeamMemberType[] {
  return members.filter((member) => member.homeUnitMember);
}

/**
 * The Team tab's display list. The browsable roster is the run's whole service-line team
 * (WORK-6529) — every member, narrowed by the unit/role/FTE/search filters. The unit filter
 * (defaulted to the reviewed unit) scopes it to one unit; "All units" shows the full service
 * line, including cross-unit members homed to a sibling unit. A "View in Team" Needs
 * Attention card sets `workerIds` to the staff it flagged — which can span units (the
 * "staffed outside their home unit" card) — so that path filters by id and skips the unit
 * filter (the flagged set is explicit), while role/FTE/search still narrow within it.
 */
export function selectTeamDisplay(
  members: ScheduleTeamMemberType[],
  {
    workerIds,
    unitFilter,
    roleFilter,
    fteFilter,
    query,
  }: {
    workerIds: readonly string[];
    unitFilter: ScheduleTeamUnitFilterType;
    roleFilter: ScheduleTeamRoleFilterType;
    fteFilter: ScheduleTeamFteFilterType;
    query: string;
  }
): ScheduleTeamMemberType[] {
  const base =
    workerIds.length > 0
      ? filterTeamByWorkerIds(members, workerIds)
      : filterTeamByUnit(members, unitFilter);

  return sortTeamByName(
    filterTeamByQuery(
      filterTeamByFte(filterTeamByRole(base, roleFilter), fteFilter),
      query
    )
  );
}

/**
 * A worker is below FTE when they have contracted hours left to schedule. Hours are
 * null until computed, so nobody matches the Below-FTE filter yet.
 */
export function isBelowFte(member: ScheduleTeamMemberType): boolean {
  if (member.remainingHours === null) return false;
  return member.remainingHours > 0;
}

/** A worker is above FTE when they are scheduled past their contracted hours. */
export function isAboveFte(member: ScheduleTeamMemberType): boolean {
  if (member.remainingHours === null) return false;
  return member.remainingHours < 0;
}

/** Hours-to-FTE readout, e.g. "12h to FTE", "8h over FTE", "At FTE", or "—" until hours land. */
export function getFteLabel(member: ScheduleTeamMemberType): string {
  if (member.remainingHours === null) return '—';
  if (member.remainingHours < 0)
    return `${Math.abs(member.remainingHours)}h over FTE`;
  if (member.remainingHours === 0) return 'At FTE';
  return `${member.remainingHours}h to FTE`;
}

/**
 * Hover detail behind the "Xh to FTE" readout: the contracted ("Expected") vs worked
 * ("Scheduled") hours, e.g. "Expected: 80h · Scheduled: 72h". Null when hours aren't
 * computed yet (the label shows "—", so there is nothing to expand).
 */
export function getFteTooltip(member: ScheduleTeamMemberType): string | null {
  if (member.fteHours === null || member.scheduledHours === null) return null;
  return `Expected: ${fteHoursLabel(member.fteHours)} · Scheduled: ${fteHoursLabel(member.scheduledHours)}`;
}
